import { type HTMLAttributes } from 'react';
import {
  type DateValue,
  RangeCalendarContext,
  useSlottedContext,
} from 'react-aria-components';

import { cx } from '@/utils/cx.ts';

interface RangePresetButtonProps extends HTMLAttributes<HTMLButtonElement> {
  value: { start: DateValue; end: DateValue };
  /** The current selected range value, used when the button is outside RangeCalendarContext */
  currentValue?: { start: DateValue; end: DateValue } | null;
}

export const RangePresetButton = ({
  value,
  currentValue,
  className,
  children,
  ...props
}: RangePresetButtonProps) => {
  const context = useSlottedContext(RangeCalendarContext);

  // Use context value if available (when inside RangeCalendar), otherwise use currentValue prop
  const selectedRange = context?.value ?? currentValue;
  const isSelected =
    selectedRange?.start?.compare(value.start) === 0 &&
    selectedRange?.end?.compare(value.end) === 0;

  return (
    <button
      type='button'
      aria-pressed={isSelected}
      {...props}
      className={cx(
        'cursor-pointer rounded-md px-3 py-2 text-left text-sm font-medium outline-focus-ring transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2',
        isSelected
          ? 'bg-active text-secondary_hover hover:bg-secondary_hover'
          : 'text-secondary hover:bg-primary_hover hover:text-secondary_hover',
        className
      )}
    >
      {children}
    </button>
  );
};
